// src/Education.js

import React, { useEffect, useMemo } from "react";
import { FaPlay, FaPause, FaClock } from "react-icons/fa";
import { usePlayer } from "./PlayerContext";
import { useData } from "./DataProvider";
import "./Education.css";

// Parse semester string to sortable value
function semesterToNumber(semesterYear) {
  if (!semesterYear) return 0;
  const parts = semesterYear.split(' ');
  if (parts.length < 2) return 0;
  const season = parts[0].toLowerCase();
  const year = parseInt(parts[1], 10);
  const seasonValue = season === 'fall' ? 0 : 0.5;
  return year + seasonValue;
}

export default function Education() {
  const { educationData: fetchedEduData, courseSongsData: courseSongs } = useData();

  const educationData = fetchedEduData?.educationData || [];

  const courses = useMemo(() => {
    return [...(fetchedEduData?.courses || [])].sort((a, b) =>
      semesterToNumber(a.semesterYear) - semesterToNumber(b.semesterYear)
    );
  }, [fetchedEduData]);

  const { playTrack, currentTrack, isPlaying, togglePlay, setPlaylist, setPagePlaylist } = usePlayer();

  // Cleanup page playlist on unmount
  useEffect(() => {
    return () => setPagePlaylist(null);
  }, [setPagePlaylist]);

  // Register page playlist when courses and songs are loaded
  useEffect(() => {
    if (courses.length > 0 && Object.keys(courseSongs).length > 0) {
      const playlistData = courses.map(c => {
        const s = courseSongs[c.code];
        return s ? { trackId: s.trackId, title: s.title, artist: s.artist, thumbnail: s.thumbnail || '' } : null;
      }).filter(Boolean);
      setPagePlaylist(playlistData);
    }
  }, [courses, courseSongs, setPagePlaylist]);

  const playCourse = (courseCode, idx) => {
    const song = courseSongs[courseCode];
    if (song) {
      // Build playlist for next/prev
      const playlistData = courses.map(c => {
        const s = courseSongs[c.code];
        return s ? { trackId: s.trackId, title: s.title, artist: s.artist, thumbnail: s.thumbnail || '' } : null;
      }).filter(Boolean);
      setPlaylist(playlistData);
      playTrack(song.trackId, { title: song.title, artist: song.artist, thumbnail: song.thumbnail || '' }, playlistData, idx);
    }
  };

  const playAll = () => {
    if (courses.length > 0) {
      const firstCourse = courses[0];
      playCourse(firstCourse.code, 0);
    }
  };

  const handleTogglePlayAll = () => {
    // Check if currently playing a course song
    const isPlayingCourse = courses.some(c => courseSongs[c.code]?.trackId === currentTrack);
    if (isPlayingCourse && isPlaying) {
      togglePlay();
    } else {
      playAll();
    }
  };

  // Check if currently playing from this page
  const isPlayingFromHere = courses.some(c => courseSongs[c.code]?.trackId === currentTrack);

  return (
    <div className="education-container">
      {/* Education Header */}
      <div className="education-header">
        <h2 className="education-title">Education</h2>
      </div>

      {/* Schools as Album Cards */}
      <div className="education-grid">
        {educationData.map((edu, idx) => (
          <div key={idx} className="edu-card">
            <img src={edu.logo} alt={edu.school} className="edu-logo" />
            <div className="edu-content">
              <div className="edu-school">{edu.school}</div>
              <div className="edu-degree">{edu.degree}</div>
              <div className="edu-meta">{edu.meta}</div>
              <div className="edu-description">{edu.desc}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Coursework as Single Playlist */}
      <div className="coursework-section">
        <div className="playlist-section">
          {/* Playlist Header */}
          <div className="playlist-header">
            <div className="playlist-cover" style={{ background: '#5c1b1b' }}>
              <span className="playlist-icon">📖</span>
            </div>
            <div className="playlist-info">
              <span className="playlist-type">Playlist</span>
              <h3 className="playlist-name">Coursework Playlist</h3>
              <span className="playlist-meta">{courses.length} courses • Divyesh</span>
            </div>
            <button className="playlist-play-btn" onClick={handleTogglePlayAll}>
              {isPlayingFromHere && isPlaying ? <FaPause size={18} /> : <FaPlay size={18} style={{ marginLeft: '2px' }} />}
            </button>
          </div>

          {/* Track List Header */}
          <div className="course-list-header">
            <div>#</div>
            <div>Course</div>
            <div>Semester</div>
            <div><FaClock size={14} /></div>
          </div>

          {/* Tracks */}
          <div className="course-list">
            {courses.map((course, idx) => {
              const song = courseSongs[course.code];
              const isThisPlaying = song && currentTrack === song.trackId;

              return (
                <div
                  key={course.code}
                  className={`course-row ${isThisPlaying ? 'playing' : ''}`}
                  onClick={() => playCourse(course.code, idx)}
                >
                  <div className="course-index">
                    {isThisPlaying && isPlaying ? (
                      <div className="playing-indicator">
                        <span></span><span></span><span></span>
                      </div>
                    ) : (
                      <>
                        <span className="course-num">{idx + 1}</span>
                        <FaPlay className="course-play-icon" size={12} />
                      </>
                    )}
                  </div>
                  <div className="course-info">
                    <div className={`course-title ${isThisPlaying ? 'text-green' : ''}`}>
                      {course.title}
                    </div>
                    <div className="course-code">{course.code}</div>
                  </div>
                  <div className="course-semester">{course.semesterYear}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

    </div>
  );
}
